import { Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import type { EpayAccount } from 'api/payment/types';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import PaymentCard from 'ui-kit/components/card/PaymentCard';
import CardIcon from './Icons/CardIcon';
import PaymentEmptyIcon from './Icons/PaymentEmptyIcon';
import PaymentMethodCard from './PaymentMethodCard';

interface PaymentCardsProps {
  paymentMethodData: EpayAccount[];
  handleOpenDrawer: () => void;
  onEdit?: (accountAliasId: string, isAch: boolean) => void;
  onDelete?: (accountAliasId: string, isAch: boolean, accountName: string, paymentType: string) => void;
  isPaymentMethodView?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    emptyStateWrap: {
      width: '100%',
      height: '100%',
      minHeight: '60vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      marginTop: -theme.spacing(3),
    },
    cardtext: {
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
    },
    responsiveCard: {
      transition: 'all 0.3s ease-in-out',
      maxWidth: '340px',
    },
  }),
);

const PaymentCards = ({
  paymentMethodData,
  handleOpenDrawer,
  onEdit,
  onDelete,
  isPaymentMethodView = false,
}: PaymentCardsProps) => {
  const classes = useStyles();

  if (paymentMethodData?.length === 0)
    return (
      <Grid item sm={12} className={classes.emptyStateWrap}>
        {isPaymentMethodView ? <PaymentEmptyIcon /> : <CardIcon />}
        <div className={classes.cardtext}>
          <Typography variant="h4">No Payment Methods On File</Typography>
          <Typography variant="body1" color="textSecondary">
            Would you like to add one now?
          </Typography>
        </div>
        <AgentNetButton
          color="primary"
          size="small"
          plusIcon={isPaymentMethodView}
          variant="contained"
          onClick={handleOpenDrawer}
          data-testid="payment-method-button-add-payment-method-bottom"
        >
          Add Payment Method
        </AgentNetButton>
      </Grid>
    );

  return (
    <>
      {paymentMethodData.map((data, index) => {
        const {
          paymentType,
          accountAliasId,
          paymentMethodId,
          cardBrand,
          nameOnAccount,
          nickName,
          category,
          accountType,
          expirationDate,
          accountNumber,
          isAutopay,
          isDefault,
        } = data;
        const ePayType = paymentType === 'CC' ? (cardBrand as string) : paymentType;
        const accountTypeLabel = `${category ?? ''} ${accountType ?? ''}`.trim();

        return (
          <Grid item key={accountAliasId} className={classes.responsiveCard} sm={6} md={4}>
            {isPaymentMethodView ? (
              <PaymentMethodCard
                title={nickName}
                subtitle={nameOnAccount}
                accountType={accountTypeLabel}
                ePayType={ePayType}
                goodThru={expirationDate}
                cardEnding={accountNumber}
                isAutopay={isAutopay}
                isDefault={isDefault}
                paymentMethodId={paymentMethodId}
                index={index}
              />
            ) : (
              <PaymentCard
                ePayName={nameOnAccount}
                ePayNickName={nickName as string}
                accountType={accountTypeLabel}
                ePayType={ePayType}
                goodThru={expirationDate}
                cardEnding={accountNumber}
                accountAliasId={accountAliasId}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            )}
          </Grid>
        );
      })}
    </>
  );
};

export default PaymentCards;
